import type {GridRowSelectionModel} from '@mui/x-data-grid'
import {createSlice} from '@reduxjs/toolkit'
import type {PayloadAction} from '@reduxjs/toolkit'
import {getUniqueValuesFromArray, getUrlParams} from '@app/util-functions'
import type {
  OverlayedViewSignalInterface,
  SignalInterface,
  SliceStateProps,
  ViewsSignalInterface,
} from '@shared/interfaces/signal'

type InitialStateProps = SliceStateProps

const initialState: InitialStateProps = {
  all: [],
  active: [],
  overlayed: [],
  activeSignalsViews: [],
  order: [],
  url: '',
  activeSignalsIds: [],
}

export const signals = createSlice({
  name: 'signals',
  initialState,
  reducers: {
    setAllSignals: (state, action: PayloadAction<SignalInterface[]>) => ({
      ...state,
      all: action.payload,
    }),
    setGlobalActiveSignals: (
      state,
      action: PayloadAction<
        | OverlayedViewSignalInterface[]
        | SignalInterface[]
        | ViewsSignalInterface[]
        | GridRowSelectionModel
        | string[]
        | null
      >,
    ) => {
      if (action.payload) {
        // Handle MUI X DataGrid v8 selection model format: {type: 'include'|'exclude', ids: Set<GridRowId>}
        let payload:
          | OverlayedViewSignalInterface[]
          | SignalInterface[]
          | ViewsSignalInterface[]
          | string[]

        if (
          typeof action.payload === 'object' &&
          'ids' in action.payload &&
          action.payload.ids instanceof Set
        ) {
          // v8 format: convert Set to array of strings
          payload = Array.from(action.payload.ids) as string[]
        } else if (Array.isArray(action.payload)) {
          // Array format (v7 or direct array)
          payload = action.payload
        } else {
          // Fallback to empty array
          payload = []
        }

        const activeSignals = payload
          .map((item) => {
            if (!item) {
              return null
            }
            let signal
            if (typeof item === 'string') {
              signal = state.all.find((i: SignalInterface) => i.id === item)
            } else if ((item as SignalInterface).id) {
              signal = state.all.find((i: SignalInterface) => i.id === (item as SignalInterface).id)
            } else if ((item as ViewsSignalInterface).signal) {
              // Handles selected signals from views
              signal = state.all.find(
                (i: SignalInterface) => i.id === (item as ViewsSignalInterface).signal,
              )
            }
            return signal
          })
          .filter((item) => item !== undefined && item !== null)
        if (activeSignals) {
          const signalsIds = activeSignals.map((item) => (item as SignalInterface)?.id)
          const signalsURL = getUrlParams(
            activeSignals.map((item) => (item as SignalInterface)?.id),
            'signal',
          )
          return {
            ...state,
            active: getUniqueValuesFromArray(activeSignals) as SignalInterface[],
            url: activeSignals[0] ? signalsURL : '',
            overlayed: activeSignals.map((item) => ({signal: (item as SignalInterface)?.id})),
            activeSignalsIds: signalsIds,
          }
        }
      }
      return {
        ...state,
        active: action.payload as SignalInterface[],
      }
    },
  },
})

// Action creators are generated for each case reducer function
export const {setAllSignals, setGlobalActiveSignals} = signals.actions
export default signals.reducer
